import axios, { AxiosInstance, AxiosResponse } from 'axios';

import type { UpdateBillingAddressDto } from '@src/api-clients/reservation-api-client';
import { TimeOptions } from '@src/types/api/models';
import { ApiResponse } from '@src/types/api/response/api-response';

/**
 * Get type from reservation API open API spec
 */
export type TripV2 = {
  billingAddress: Omit<UpdateBillingAddressDto, 'travelPurpose'>;
  travelPurpose: UpdateBillingAddressDto['travelPurpose'];
  guests: { id: number; isMainGuest: boolean }[];
};

export class TripV2Service {
  constructor(private readonly networkService: AxiosInstance) {}

  private async errorHandlerWrapper<T>(request: Promise<AxiosResponse<T>>) {
    try {
      return await request;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        const message = `ERROR when calling ${e.request?.path} - error: ${JSON.stringify(e.response?.data)}`;
        // eslint-disable-next-line no-console
        console.log(message);
      }
      throw e;
    }
  }

  async getTimeOptions(tripId: string): Promise<TimeOptions> {
    const response = await this.errorHandlerWrapper(
      this.networkService.get<ApiResponse<TimeOptions>>(`/reservation/v2/${tripId}/time-options`),
    );
    return response.data.data;
  }

  async getTripById(tripId: string, locale = 'en'): Promise<TripV2 | undefined> {
    const response = await this.errorHandlerWrapper<ApiResponse<TripV2>>(
      this.networkService.get<ApiResponse<TripV2>>(`/reservation/v2/${tripId}?locale=${locale}`),
    );

    if (!response) {
      return undefined;
    }

    return response.data.data;
  }

  async getMaskedPhoneNumber(tripId: string): Promise<{ maskedPhoneNumber: string }> {
    const response = await this.errorHandlerWrapper(
      this.networkService.get<ApiResponse<{ maskedPhoneNumber: string }>>(
        `/reservation/v2/masked-phone-number?reservationId=${tripId}`,
      ),
    );
    return response.data.data;
  }

  async authorizeViaPhoneNumber(tripId: string, lastFourPhoneNumberDigits: string): Promise<{ key: string }> {
    const response = await this.errorHandlerWrapper(
      this.networkService.post<ApiResponse<{ key: string }>>('/reservation/v2/authorize-via-phone-number', {
        reservationId: tripId,
        lastFourPhoneNumberDigits,
      }),
    );
    return response.data.data;
  }

  async authorizeViaLastName(tripId: string, lastName: string): Promise<{ key: string }> {
    const response = await this.errorHandlerWrapper(
      this.networkService.post<ApiResponse<{ key: string }>>('/reservation/v2/authorize-via-last-name', {
        reservationId: tripId,
        lastName,
      }),
    );
    return response.data.data;
  }
}
